import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { useQuery } from '@apollo/client';
import { IconButton } from '@mui/material';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Fade from '@mui/material/Fade';
import Popper from '@mui/material/Popper';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import { alpha, keyframes } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';

import AccountIcon from '../../assets/icons/AccountIcon';
import BellIcon from '../../assets/icons/BellIcon';
import CloseIcon from '../../assets/icons/CloseIcon';
import MenuIcon from '../../assets/icons/MenuIcon';
import { ConditionalLink } from '../../components/ConditionalLink';
import IKImage from '../../components/IKImage/IKImage';
import NavigationOverlay from '../../components/NavigationOverlay/NavigationOverlay';
import { SearchInput } from '../../components/SearchInput';
import { CustomButton } from '../../components/UI/CustomButton';
import { SecondaryButton } from '../../components/UI/SecondaryButton';
import { UserInfoCtx } from '../../containers/CommonLayout/CommonLayout';
import { CourseDropdownItem } from '../../containers/Layout/Layout';
import useScriptLoader, { SCRIPTS } from '../../context/ScriptLoader/useScriptLoader';
import { GET_LIST_BASE_WITH_SORT } from '../../graphql/analytics';
import { GET_HAS_NEWS } from '../../graphql/local';
import { GET_TWO_LATEST_VIDEOS } from '../../graphql/videos';
import useClickOutside from '../../pages/AnalyticsPage/components/PickerWithRanges/useClickOutside';
import { USER_ROLES } from '../../pages/const';
import styled, { css } from '../../styled';
import {
  colorsPalette,
  MIDDLE_MARGIN,
  MIDDLE_MARGIN_PX,
  SMALL_MARGIN,
  SMALL_MARGIN_PX,
  TINY_MARGIN_PX,
  useIsWidthUp,
} from '../../theme';
import {
  Navigation,
  NavigationItem,
  NavigationItemType,
  Query,
  QueryListBaseVideoCategoriesArgs,
  SortOrder,
} from '../../types';
import { checkIsIframed, getCourseUrl, replaceByIndex, translate } from '../../utils/helpers';
import { useAuth, useEventListener } from '../../utils/hooks';
import useIsIframed from '../../utils/hooks/useIsIframed';
import useOverflowHidden from '../../utils/hooks/useOverflowHidden';
import LogoutMegaMenuFooter from './components/LogoutMegaMenuFooter';
import { MenuItems } from './components/MenuItems';
import Notifications from './components/Notifications';
import SafeLink from './components/SafeLink/SafeLink';
import { UserInfoDropdown } from './components/UserInfoDropdown';
import { formatMegaMenuItem, LOGGED_IN_ITEMS, PROFILE_LINKS } from './const';
import usePaginatedNotifications from './usePaginatedNotifications';

export const LOGO_TRANSFORMATION = [
  {
    height: 120,
  },
];

const CATEGORIES_FILTER = {
  sort: [
    {
      field: 'title',
      order: SortOrder.Asc,
    },
  ],
};

const getButtonProps = (url, location, loginWithPopup) => {
  if (url === '/login') {
    if (checkIsIframed()) {
      return {
        to: '/login',
        onClick: (e) => {
          e.preventDefault();
          loginWithPopup();
        },
      };
    } else {
      return {
        to: {
          pathname: '/login',
          state: { prevPath: location.pathname, prevSearch: location.search },
        },
      };
    }
  } else {
    return {
      to: url,
    };
  }
};

interface HeaderProps {
  logoUrl?: string | null;
  onlyLogo?: boolean;
  navigation?: Navigation;
  assignedLearningsName?: string;
  dropdownCourses?: CourseDropdownItem[];
  isHeaderHidden?: boolean;
}

export const Header: React.FC<HeaderProps> = ({
  logoUrl,
  navigation,
  onlyLogo = false,
  assignedLearningsName,
  dropdownCourses,
  isHeaderHidden = false,
}) => {
  const { t } = useTranslation();
  const [pageYOffset, setPageYOffset] = useState(0);
  const [isSearchOpen, setSearchOpenState] = useState(false);
  const [menuOpened, setMenuOpened] = useState(false);
  const [userInfoOpened, setUserInfoOpened] = useState(false);
  const location = useLocation();
  const { userInfo } = useContext(UserInfoCtx);
  const [showNotifications, setShowNotifications] = useState(false);
  const notificationsButtonRef = useRef(null);
  const [arrowElement, setArrowElement] = useState();
  const { isAuthenticated, loginWithRedirect, loginWithPopup, logout } = useAuth();
  const isUserComLoaded = useScriptLoader(SCRIPTS.USER_COM);
  useClickOutside('#notificationsCenter', () => setShowNotifications(false));

  const isTablet = !useIsWidthUp('xl');
  const isMobile = !useIsWidthUp('md');
  const userName = userInfo?.name;
  const isIframed = useIsIframed();

  const toggleMenu = () => setMenuOpened((prevState) => !prevState);
  const toggleUserInfo = () => setUserInfoOpened((prevState) => !prevState);
  useOverflowHidden(isMobile && showNotifications);

  const { data } = useQuery(GET_HAS_NEWS);
  const hasNews = data?.hasNews;

  const { data: baseCategoriesData } = useQuery<
    Pick<Query, 'listBaseVideoCategories'>,
    QueryListBaseVideoCategoriesArgs
  >(GET_LIST_BASE_WITH_SORT, {
    skip: !userInfo,
    variables: CATEGORIES_FILTER,
  });

  const { data: twoLatestVideosData } = useQuery<
    Pick<Query, 'getLastAddedTwoVideosFromDifferentExperts'>
  >(GET_TWO_LATEST_VIDEOS, {
    skip: !userInfo,
  });

  const {
    data: announcementsData,
    loading: loadingAnnouncementsData,
    ableFetchMore,
    fetchMore,
  } = usePaginatedNotifications();

  const notificationsList = announcementsData?.getAnnouncementsList?.data || [];
  const hasNewNotifications = notificationsList?.some(
    (notification) => notification.is_highlighted,
  );

  useEffect(() => {
    if (menuOpened) {
      setUserInfoOpened(false);
    }
  }, [menuOpened]);

  useEffect(() => {
    if (userInfoOpened) {
      setMenuOpened(false);
    }
  }, [userInfoOpened]);

  const navigationItems: NavigationItem[] = useMemo(() => {
    return navigation?.items
      ? (navigation?.items.filter(
          (item) =>
            item?.item_type === NavigationItemType.Item ||
            item?.item_type === NavigationItemType.MegaMenuItem,
        ) as NavigationItem[])
      : [];
  }, [navigation]);

  const teamName = useMemo(() => {
    if (userInfo?.teams && userInfo.teams[0]) {
      return userInfo?.teams[0]?.name;
    }

    return null;
  }, [userInfo]);

  const toggleSearch = () => {
    setSearchOpenState(!isSearchOpen);
  };

  const handleOpenUserComWidget = () => {
    //@ts-ignore
    if (window?.userengage) {
      //@ts-ignore
      window.userengage('widget.open');
    }
  };

  const handleClickContact = () => {
    handleOpenUserComWidget();
    toggleUserInfo();
  };

  useEventListener(
    'scroll',
    () => {
      if (window) {
        const newPageYOffset = window.pageYOffset;

        if (pageYOffset === 0 && newPageYOffset > 0) {
          setPageYOffset(newPageYOffset);
        } else if (pageYOffset > 0 && newPageYOffset === 0) {
          setPageYOffset(newPageYOffset);
        }
      }
    },
    undefined,
    { passive: true },
  );

  const buttonData = navigationItems?.find((item) => item.type === 'Button');
  const filledButtonData = navigationItems?.find((item) => item.type === 'FilledButton');
  const linksData = navigationItems?.filter(
    (item) => item.type === 'Link' || item.type === 'MegaMenu',
  );
  const userData = navigationItems?.find((item) => item.type === 'User');
  const loginButton = navigationItems?.find((item) => item.type === 'LoginButton');

  const chosenItems = useMemo(() => {
    if (userInfo) {
      const arr = LOGGED_IN_ITEMS.map((item) => {
        if (item.type === 'MegaMenu') {
          return formatMegaMenuItem(
            item,
            t,
            hasNews,
            baseCategoriesData?.listBaseVideoCategories,
            twoLatestVideosData?.getLastAddedTwoVideosFromDifferentExperts,
          );
        }

        return {
          ...item,
          title: translate(`header.${item.title}`),
        };
      });

      if (
        (userInfo?.teams && userInfo.teams.length > 0) ||
        userInfo?.role === USER_ROLES.CompanyOwner ||
        userInfo?.role === USER_ROLES.CompanyManager
      ) {
        const assignedLearningTitle = assignedLearningsName
          ? assignedLearningsName
          : t('header.assigned');
        const url = userInfo?.is_manager ? '/assigned-learning/dashboard' : '/assigned-learning';

        arr.push({
          url,
          title: `${assignedLearningTitle} ${t('header.learning')}`,
          subItems: [],
          type: '',
        });
      }

      return arr;
    } else {
      let loggedOutLinksData: NavigationItem[] = [];

      const indexOfCourseLink = linksData.findIndex((item) => item?.url === '/courses');

      if (
        indexOfCourseLink !== -1 &&
        dropdownCourses &&
        dropdownCourses?.length === 1 &&
        dropdownCourses[0]
      ) {
        loggedOutLinksData = replaceByIndex(linksData, indexOfCourseLink, {
          ...loggedOutLinksData[indexOfCourseLink],
          url: getCourseUrl(dropdownCourses[0]?.slug) || '',
        });
      } else {
        loggedOutLinksData = linksData;
      }

      return loggedOutLinksData;
    }
  }, [
    linksData,
    userInfo,
    assignedLearningsName,
    t,
    dropdownCourses,
    hasNews,
    baseCategoriesData,
    twoLatestVideosData,
  ]);

  const handleClickMenuLink = useCallback(() => {
    setMenuOpened(false);
  }, []);

  const handleLogout = useCallback(async () => {
    if (isUserComLoaded && window?.UE?.resetAuth) {
      await window?.UE?.resetAuth({
        apiKey: process.env.REACT_APP_USERCOM_API_KEY,
      });
    }

    logout(
      checkIsIframed()
        ? {
            returnTo: `${window?.location?.origin}/?logout`,
          }
        : undefined,
    );
  }, [logout, isUserComLoaded]);

  const handleLogin = () => {
    const loginOptions = {
      redirectUri:
        location.pathname !== '/home' ? `${location.pathname}${location.search}` : '/home',
      isDirectUrl: location.pathname === '/home' || location.pathname === '/',
    };

    if (checkIsIframed()) {
      loginWithPopup();
      setMenuOpened(false);
    } else {
      loginWithRedirect(loginOptions);
    }
  };

  //used to close mobile menu when click on link
  useEffect(() => {
    setMenuOpened(false);
  }, [location]);

  const pageYOffsetGreaterZero = pageYOffset > 0;

  return (
    <StyledAppBar
      position="fixed"
      color="transparent"
      elevation={0}
      isHeaderHidden={isHeaderHidden}
      pageYOffsetGreaterZero={pageYOffsetGreaterZero}
    >
      <StyledToolBar
        className={pageYOffsetGreaterZero ? 'scrolled-toolbar' : ''}
        pageYOffsetGreaterZero={pageYOffsetGreaterZero}
      >
        <LeftPart>
          <LogoWrapper
            pageYOffsetGreaterZero={pageYOffsetGreaterZero}
            openSearch={isSearchOpen}
            className={pageYOffsetGreaterZero ? 'header_logo_small' : 'header_logo'}
          >
            <StyledConditionalLink withLink={!onlyLogo} to={userInfo ? '/home' : '/'}>
              {logoUrl && (
                <IKImage
                  path={logoUrl || ''}
                  draggable={false}
                  alt={'logo'}
                  transformation={LOGO_TRANSFORMATION}
                  // We want to disable srcset for logo for now. It causes an issue when we have custom CSS styles for diff logo and they change the width
                  // srcset={[{ width: 141 }, { width: 175 }]}
                  // sizes={`(min-width:${BREAKPOINTS_VALUES["sm"]}px) 175px, 141px`}
                />
              )}
            </StyledConditionalLink>
          </LogoWrapper>
          {!isSearchOpen && !onlyLogo && (
            <TopNavMenu>
              <MenuItems
                items={chosenItems}
                hasSubscription={Boolean(userInfo?.has_subscription)}
                isMobile={isTablet}
                scrolled={pageYOffset > 0}
                isManager={userInfo?.is_manager}
              />
            </TopNavMenu>
          )}
        </LeftPart>
        {onlyLogo ? (
          <div></div>
        ) : (
          <>
            <TopBar className={isSearchOpen ? 'openSearch' : ''}>
              {menuOpened ? (
                <StyledCloseIcon onClick={toggleMenu} />
              ) : (
                <StyledMenuIcon onClick={toggleMenu} />
              )}
              {!userInfo && (
                <>
                  <div>
                    {buttonData && !isMobile && (
                      <JoinBtnLink {...getButtonProps(buttonData?.url, location, loginWithPopup)}>
                        <JoinBtn>{buttonData?.title}</JoinBtn>
                      </JoinBtnLink>
                    )}
                    {loginButton && !isMobile && (
                      <JoinBtn onClick={handleLogin}>{loginButton?.title}</JoinBtn>
                    )}
                    {filledButtonData && !isMobile && (
                      <JoinBtnLink to={filledButtonData?.url || '#'}>
                        <SecondaryJoinBtn>{filledButtonData?.title}</SecondaryJoinBtn>
                      </JoinBtnLink>
                    )}
                  </div>
                  {userData && (
                    <AccLink
                      href={userData?.url || '#'}
                      className={isSearchOpen ? 'openSearch' : ''}
                    >
                      <AccountIcon
                        color={colorsPalette.white}
                        aria-label="account of current user"
                      />
                    </AccLink>
                  )}
                </>
              )}
              {userInfo && (
                <>
                  <Username className="header_username">{userName}</Username>
                  <UserInfoDropdown
                    teamName={teamName}
                    scrolled={pageYOffset > 0}
                    handleOpenUserComWidget={handleOpenUserComWidget}
                    handleLogout={handleLogout}
                    onClick={toggleUserInfo}
                  />
                  {Boolean(notificationsList?.length) && (
                    <div id="notificationsCenter">
                      <BellButton
                        disabled={loadingAnnouncementsData}
                        ref={notificationsButtonRef}
                        onClick={() => setShowNotifications((old) => !old)}
                        opened={showNotifications}
                      >
                        <BellWrapper marked={hasNewNotifications}>
                          <StyledBellIcon />
                        </BellWrapper>
                      </BellButton>
                      <StyledPopper
                        arrow
                        disablePortal
                        anchorEl={notificationsButtonRef.current}
                        open={showNotifications}
                        modifiers={[
                          {
                            name: 'arrow',
                            options: { element: arrowElement },
                          },
                          !isMobile && { name: 'offset', options: { offset: [0, 20] } },
                          !isMobile && {
                            name: 'preventOverflow',
                            options: { padding: MIDDLE_MARGIN },
                          },
                        ]}
                        transition
                      >
                        {({ TransitionProps }) => (
                          <Fade {...TransitionProps} timeout={350}>
                            <div>
                              <Arrow ref={setArrowElement} />
                              <Notifications
                                items={notificationsList}
                                fetchMore={fetchMore}
                                disabledPagination={loadingAnnouncementsData || !ableFetchMore}
                                closeHandler={() => setShowNotifications(false)}
                              />
                            </div>
                          </Fade>
                        )}
                      </StyledPopper>
                    </div>
                  )}
                  <SearchInput
                    wrapperCss={searchWrapperCss(isSearchOpen)}
                    isOpen={isSearchOpen}
                    toggle={toggleSearch}
                  />
                </>
              )}
            </TopBar>
            <NavigationOverlay opened={menuOpened} startBreakpoint="xl">
              <>
                <OverlayContent className={userInfo ? 'without-last-border' : ''}>
                  <OverlayContainer>
                    <MenuItems
                      wrapperCss={userInfo && buttonData ? menuItemsWrapperCss : undefined}
                      items={chosenItems}
                      isManager={userInfo?.is_manager}
                      hasSubscription={Boolean(userInfo?.has_subscription)}
                      isMobile={isTablet}
                      handleClickLink={handleClickMenuLink}
                    />
                  </OverlayContainer>

                  {isAuthenticated ? (
                    !isIframed ? (
                      <OverlayBottomLink onClick={handleLogout}>
                        {t('header.logout')}
                      </OverlayBottomLink>
                    ) : null
                  ) : (
                    <StyledLogoutMegaMenuFooter
                      mainBtnProps={getButtonProps(buttonData?.url, location, loginWithPopup)}
                      mainBtnText={buttonData?.title}
                      additionalBtnLink={filledButtonData?.url}
                      additionalBtnText={filledButtonData?.title}
                    />
                  )}
                </OverlayContent>
              </>
            </NavigationOverlay>
            {userInfo && (
              <NavigationOverlay opened={userInfoOpened} startBreakpoint="xl">
                <OverlayContent className={userInfo ? 'without-last-border' : ''}>
                  {userInfo && (
                    <>
                      <OverlayMainContent>
                        <UserInfoWrapper>
                          <Username>{userName}</Username>
                          {teamName && <Team>{teamName}</Team>}
                        </UserInfoWrapper>
                        <AccordionLinks>
                          {PROFILE_LINKS.map((item) => (
                            <LinkWrapper className="header_linkWrapper" key={item.url}>
                              <StyledLink to={item.url} onClick={() => setUserInfoOpened(false)}>
                                {t(item.title)}
                              </StyledLink>
                            </LinkWrapper>
                          ))}
                          {
                            // @ts-ignore
                            !!window?.userengage && (
                              <LinkWrapper>
                                <StyledLinkDiv onClick={handleClickContact}>
                                  {t('header.contact')}
                                </StyledLinkDiv>
                              </LinkWrapper>
                            )
                          }
                        </AccordionLinks>
                      </OverlayMainContent>
                    </>
                  )}
                  {!isIframed && (
                    <OverlayBottomLink onClick={handleLogout}>
                      {t('header.logout')}
                    </OverlayBottomLink>
                  )}
                </OverlayContent>
              </NavigationOverlay>
            )}
            <HeaderBackground
              show={menuOpened || userInfoOpened || (isMobile && showNotifications)}
              scrolled={pageYOffset > 0}
            ></HeaderBackground>
          </>
        )}
      </StyledToolBar>
      {isSearchOpen && <FullscreenOverlay onClick={toggleSearch} />}
    </StyledAppBar>
  );
};

export default Header;

const FullscreenOverlay = styled('div')`
  position: fixed;
  width: 100%;
  height: 100%;
  background: ${alpha(colorsPalette.blueDark, 0.82)};
`;

const createMenuIcon = (tag) => styled(tag)`
  width: 24px;
  min-width: 24px;
  height: 24px;
  margin-left: ${SMALL_MARGIN_PX};

  ${({ theme }) => theme.breakpoints.up('md')} {
    width: 36px;
    min-width: 36px;
    height: 36px;
  }

  ${({ theme }) => theme.breakpoints.up('xl')} {
    display: none;
  }
`;

const StyledMenuIcon = createMenuIcon(MenuIcon);
const StyledCloseIcon = createMenuIcon(CloseIcon);

const StyledLogoutMegaMenuFooter = styled(LogoutMegaMenuFooter, {
  shouldForwardProp: (prop) => prop !== 'wrapperWidth',
})`
  ${({ theme }) => theme.breakpoints.up('xl')} {
    display: none;
  }
`;

const HeaderBackground = styled('div', {
  shouldForwardProp: (prop) => prop !== 'scrolled' && prop !== 'show',
})`
  width: ${({ show }) => (show ? '100%' : '0')};
  height: ${({ scrolled }) => (scrolled ? '74px' : '114px')};
  position: absolute;
  top: 0;
  right: 0;
  background: ${colorsPalette.blueDark};
  z-index: 1200;
  transition: 0.5s ease-in-out;
`;
const StyledConditionalLink = styled(ConditionalLink)`
  max-width: 100%;
`;

const ring = keyframes`
  0% {
    transform: rotateZ(0);
  }
  13% {
    transform: rotateZ(22deg);
  }
  18% {
    transform: rotateZ(-20deg);
  }
  23% {
    transform: rotateZ(18deg);
  }
  28% {
    transform: rotateZ(-16deg);
  }
  33% {
    transform: rotateZ(14deg);
  }
  38% {
    transform: rotateZ(-12deg);
  }
  43% {
    transform: rotateZ(10deg);
  }

  50% {
    transform: rotateZ(0);
  }
  100% {
    transform: rotateZ(0);
  }
`;
const BellButton = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== 'marked' && prop !== 'opened',
})`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
  margin-right: 12px;
  border-radius: 50%;
  pointer-events: ${({ disabled }) => disabled && 'none'};
  cursor: ${({ disabled }) => !disabled && 'pointer'};
  opacity: ${({ disabled }) => disabled && '0.3'};

  ${({ theme }) => theme.breakpoints.up('md')} {
    width: 54px;
    min-width: 54px;
    height: 54px;
    margin-right: ${SMALL_MARGIN_PX};
    background: ${alpha(colorsPalette.white, 0.33)};
  }

  &:hover {
    background: ${alpha(colorsPalette.white, 0.4)};

    > div {
      animation: ${ring} 2s;
      animation-fill-mode: forwards;
    }
  }
`;
const BellWrapper = styled('div')`
  display: flex;
  transform-origin: 50% 0;
  transform: rotate(0deg);

  &:after {
    content: '';
    position: absolute;
    display: ${({ marked }) => (marked ? 'block' : 'none')};
    top: 50%;
    left: 50%;
    width: 8px;
    height: 8px;
    transform: translate(calc(-50% + 7px), calc(-50% - 7px));
    border-radius: 50%;
    background: ${colorsPalette.coral};
  }
`;
const StyledBellIcon = styled(BellIcon)`
  width: 24px;
  height: 24px;

  > * {
    stroke: ${colorsPalette.white};
    stroke-width: 7px;
  }
`;
const StyledPopper = styled(Popper)`
  position: relative;
  z-index: 10000;
  margin: 0 ${MIDDLE_MARGIN_PX};
`;
const Arrow = styled('div')`
  display: none;
  bottom: 100%;
  width: 32px;
  height: 32px;
  padding-top: 24px;
  pointer-events: none;

  ${({ theme }) => theme.breakpoints.up('md')} {
    display: block;
  }

  &:after {
    content: '';
    display: block;
    width: 32px;
    height: 32px;
    transform-origin: 50% 50%;
    transform: rotate(45deg);
    border: none;
    background: ${colorsPalette.blackSecondary};
  }
`;
const searchWrapperCss =
  (isOpened) =>
  ({ theme }) =>
    css`
      ${isOpened ? 'margin-right: ' + TINY_MARGIN_PX + ';' : ''}
      && {
        ${theme.breakpoints.up('md')} {
          margin-left: ${SMALL_MARGIN_PX};
          margin-right: ${SMALL_MARGIN_PX};
        }
      }
    `;
const menuItemsWrapperCss = css`
  margin-bottom: ${SMALL_MARGIN_PX};
`;
const StyledAppBar = styled(AppBar, {
  shouldForwardProp: (prop) => prop !== 'pageYOffsetGreaterZero' && prop !== 'isHeaderHidden',
})`
  ${({ isHeaderHidden }) => (isHeaderHidden ? 'display: none;' : '')}
  transition: background .5s ease-in-out;
  background: ${({ pageYOffsetGreaterZero }) =>
    pageYOffsetGreaterZero ? colorsPalette.blueDark : 'transparent'};
  box-shadow: ${({ pageYOffsetGreaterZero }) =>
    pageYOffsetGreaterZero ? `0px 3px 6px ${alpha(colorsPalette.black, 0.4)}` : 'none'};
  ${({ theme }) => theme.breakpoints.up('md')} {
    background: ${({ pageYOffsetGreaterZero }) =>
      pageYOffsetGreaterZero ? alpha(colorsPalette.blueDark, 0.85) : 'transparent'};
    box-shadow: none;
  }
`;
const StyledToolBar = styled(Toolbar, {
  shouldForwardProp: (prop) => prop !== 'pageYOffsetGreaterZero',
})`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  font-size: 18px;
  transition: all 0.5s ease-in-out;
  ${({ theme }) => theme.breakpoints.up('sm')} {
    padding: ${({ pageYOffsetGreaterZero }) =>
      pageYOffsetGreaterZero ? '10px 16px' : '30px 16px'};
    font-size: 16px;
  }
  ${({ theme }) => theme.breakpoints.up('md')} {
    padding: ${({ pageYOffsetGreaterZero }) =>
      pageYOffsetGreaterZero ? '10px 24px' : '30px 24px'};
  }
  ${({ theme }) => theme.breakpoints.up('xl')} {
    padding: ${({ pageYOffsetGreaterZero }) => (pageYOffsetGreaterZero ? '10px 30px' : '30px')};
  }
  ${({ theme }) => theme.breakpoints.up('ml')} {
    width: 100%;
    max-width: ${({ theme }) => theme.breakpoints.values.ml};
    margin: 0 auto;
  }
`;
const LeftPart = styled('div')`
  display: flex;
  align-items: center;
  max-width: calc(100% - 160px);
`;
const LogoWrapper = styled('div', {
  shouldForwardProp: (prop) => prop !== 'pageYOffsetGreaterZero' && prop !== 'openSearch',
})`
  display: ${({ openSearch }) => (openSearch ? 'none' : 'flex')};
  height: 45px;
  max-width: 100%;
  align-items: center;
  transition: all 0.5s ease-in-out;
  z-index: 1300;
  & img {
    max-width: 100%;
    object-fit: contain;
    height: 32px;
  }
  @media only screen and (min-width: 375px) {
    & img {
      height: 40px;
    }
  }
  @media only screen and (min-width: 425px) {
    & img {
      height: 45px;
    }
  }
  ${({ theme }) => theme.breakpoints.up('sm')} {
    display: ${({ openSearch }) => (openSearch ? 'block' : 'flex')};
    ${({ openSearch }) => (openSearch ? 'flex-grow: 0;' : '')}
    margin-right: ${SMALL_MARGIN_PX};
  }
  ${({ theme }) => theme.breakpoints.up('md')} {
    height: 60px;
    flex-grow: 1;
    flex-shrink: 1;
    flex-basis: 0%;
    min-width: 180px;
    & img {
      height: ${({ pageYOffsetGreaterZero }) => (pageYOffsetGreaterZero ? '45px' : '60px')};
      transition: all 0.5s ease-in-out;
    }
    margin-right: ${MIDDLE_MARGIN_PX};
  }
`;
const TopNavMenu = styled(Box)`
  display: none;
  ${({ theme }) => theme.breakpoints.up('xl')} {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
  }
`;
const TopBar = styled(Box)`
  display: flex;
  flex-direction: row-reverse;
  justify-content: flex-start;
  align-items: center;
  z-index: 1300;
  height: 54px;
  margin-left: ${SMALL_MARGIN_PX};
  ${({ theme }) => theme.breakpoints.up('md')} {
    margin-left: ${MIDDLE_MARGIN_PX};
  }
  &.openSearch {
    flex-grow: 1;
    padding-left: 40px;
    ${({ theme }) => theme.breakpoints.up('sm')} {
      justify-content: flex-end;
    }
    ${({ theme }) => theme.breakpoints.up('md')} {
      margin-right: 0;
      justify-content: flex-start;
    }
  }
  & > .header_username {
    display: none;
    @media only screen and (min-width: 1600px) {
      display: block;
    }
  }
`;
const JoinBtnLink = styled(SafeLink)`
  color: ${colorsPalette.white};
  margin-right: ${SMALL_MARGIN / 2}px;
  ${({ theme }) => theme.breakpoints.up('sm')} {
    &:last-child {
      margin-right: ${SMALL_MARGIN_PX};
    }
  }
  ${({ theme }) => theme.breakpoints.up('md')} {
    &:last-child {
      margin-right: 0;
    }
  }
`;
const createJoinBtnStyles = (component) => styled(component)`
  margin-bottom: ${SMALL_MARGIN_PX};
  ${({ theme }) => theme.breakpoints.up('sm')} {
    margin-bottom: 0;
  }
`;
const JoinBtn = createJoinBtnStyles(CustomButton);
const SecondaryJoinBtn = createJoinBtnStyles(SecondaryButton);
const AccLink = styled('a')`
  width: 48px;
  height: 48px;
  cursor: pointer;
  margin-left: ${SMALL_MARGIN_PX};
  margin-right: ${SMALL_MARGIN_PX};
  background: ${colorsPalette.whiteSecondary};
  padding: 12.5px;
  transition: 0.5s;
  border-radius: 999em;
  &:hover {
    box-shadow: 0px 3px 6px ${colorsPalette.black};
    background: ${({ theme }) => theme.palette.primary.main};
    & svg {
      filter: brightness(0) saturate(100%) invert(7%) sepia(28%) saturate(734%) hue-rotate(210deg)
        brightness(99%) contrast(93%);
    }
  }
  &.openSearch {
    display: none;
    ${({ theme }) => theme.breakpoints.up('md')} {
      display: block;
    }
  }
`;
const Username = styled(Typography)`
  && {
    color: ${colorsPalette.white};
    font-size: 16px;
    line-height: 1.2;
    font-weight: bold;
    margin-bottom: 2px;
    z-index: 2;
    ${({ theme }) => theme.breakpoints.up('xl')} {
      margin-left: ${SMALL_MARGIN_PX};
    }
  }
`;
const OverlayContent = styled(Box)`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;
const OverlayContainer = styled(Box)`
  margin-bottom: ${SMALL_MARGIN_PX};
`;
const OverlayBottomLink = styled('button')`
  padding: 24px;
  color: ${colorsPalette.white};
  text-transform: uppercase;
  font-weight: bold;
  font-size: 16px;
  background: ${colorsPalette.violetAnalyticsCard};
  display: block;
  width: 100vw;
  margin-left: -24px;
  margin-bottom: -24px;
  border: none;
  &:focus {
    outline: none;
  }
  ${({ theme }) => theme.breakpoints.up('sm')} {
    margin-left: -30px;
    margin-bottom: -30px;
    padding: 24px 30px;
  }
`;
const OverlayMainContent = styled('div')`
  margin-bottom: ${MIDDLE_MARGIN_PX};
`;
const UserInfoWrapper = styled(Box)`
  padding-bottom: 24px;
  border-bottom: 1px solid ${alpha(colorsPalette.white, 0.15)};
  margin-bottom: 24px;
`;
const Team = styled(Typography)`
  font-size: 14px;
  color: ${colorsPalette.gray};
  line-height: 1.5;
`;
const AccordionLinks = styled(Box)`
  display: flex;
  flex-direction: column;
  width: 100%;
  & .header_linkWrapper:last-child {
    margin-bottom: 0;
  }
`;
const LinkWrapper = styled(Box)`
  z-index: 1;
  position: relative;
  margin-bottom: 24px;
`;
const createLinkStyles = (component) => styled(component)`
  color: ${colorsPalette.white};
  text-transform: uppercase;
  font-weight: bold;
  font-size: 16px;
  line-height: 1.2;
`;
const StyledLink = createLinkStyles(Link);
const StyledLinkDiv = createLinkStyles('div');
